import { useState, useEffect } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { isStaffRole } from '@wanderlust/shared';
import { useAuth } from '../hooks/useAuth';
import { Alert, Button, Heading, IconButton, Icon, Input } from '../components/ui';

export default function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { signIn, user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  // Track whether we've mounted to avoid hydration mismatch — SSR renders
  // with loading=false but AuthProvider starts with loading=true on the client.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Show loading state while checking auth to prevent form flash,
  // but only after mount so the first render matches SSR output.
  if (mounted && loading) {
    return (
      <div className="min-h-screen bg-primary flex items-center justify-center">
        <img src="/logo.svg" alt="Wanderlust Cravings" className="max-w-[140px] animate-pulse" />
      </div>
    );
  }

  // Redirect if already authenticated — honor ?from= param for deep links
  if (user) {
    const staff = isStaffRole(user.role);
    const defaultRoute = staff ? '/admin' : '/trips';
    const from = searchParams.get('from');
    // Skip ?from=/trips for staff — their default landing is /admin
    if (from && !(staff && from === '/trips')) {
      return <Navigate to={from} replace />;
    }
    return <Navigate to={defaultRoute} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await signIn(email, password);
      // Navigation will be handled by the redirect logic above after user state updates
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary flex flex-col justify-center items-center px-6">
      <div className="w-full max-w-[400px]">
        <Link to="/" className="block mb-8">
          <img src="/logo.svg" alt="Wanderlust Cravings" className="max-w-[140px] mx-auto" />
        </Link>

        <div className="bg-white rounded-xl p-8 shadow-lg">
          <Heading level={1} size="h4" weight="semibold" color="primary" className="text-2xl text-center mb-6">
            Traveler Sign In
          </Heading>

          {error && (
            <div className="mb-4">
              <Alert variant="error">{error}</Alert>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              size="md"
              type="email"
              id="email"
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              disabled={isSubmitting}
            />

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-sm font-medium text-neutral-600">
                  Password
                </label>
                <Link to="/forgot-password" className="text-sm text-accent hover:underline">
                  Forgot password?
                </Link>
              </div>
              <Input
                size="md"
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={isSubmitting}
                rightSlot={
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    className="hover:text-neutral-800 p-1"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size="sm" />
                  </IconButton>
                }
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              isLoading={isSubmitting}
            >
              {isSubmitting ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

        </div>

        <p className="text-center text-white/60 text-sm mt-6">
          <Link to="/" className="hover:text-white transition-colors">
            Back to Home
          </Link>
        </p>
      </div>
    </div>
  );
}
